import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  RadioGroup, FormControlLabel, Radio, TextField, Alert, CircularProgress, Collapse,
} from '@mui/material';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';
import { text } from '../../theme/colors';

/**
 * "What happened to this listing?" — one tap. Opened from the project hub
 * ("Hired someone else, or pausing?"), from the 30/60-day check-in email
 * (?outcome=1) and from a paused listing's "Reopen" button. Never punitive:
 * hired elsewhere is a legitimate answer we want to hear, and a closed
 * listing reopens with "still deciding".
 */
const OUTCOMES = [
  { value: 'still_deciding', label: 'Still deciding — keep it open', hint: 'Contractors can keep quoting. We\'ll check in again in a month.' },
  { value: 'hired_elsewhere', label: 'I hired someone outside Eqaya', hint: 'Fair enough. Telling us why helps us fix what got in the way.' },
  { value: 'not_going_ahead', label: 'Not going ahead for now', hint: 'We\'ll close the listing. Nothing is lost — reopen it any time.' },
];
const REASONS = [
  { value: 'price', label: 'The quotations were more than I could spend' },
  { value: 'cash', label: 'I preferred to pay cash directly' },
  { value: 'contractor_direct', label: 'A contractor suggested dealing outside Eqaya' },
  { value: 'too_slow', label: 'It took too long to get a quotation' },
  { value: 'no_contractor', label: 'No suitable contractor came forward' },
  { value: 'other', label: 'Another reason' },
];

const ListingOutcomeDialog = ({ open, onClose, projectId, projectTitle, currentStatus, onDone }) => {
  const reopening = currentStatus === 'cancelled';
  const [outcome, setOutcome] = useState(reopening ? 'still_deciding' : '');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setOutcome(reopening ? 'still_deciding' : ''); setReason(''); setNote(''); setError(''); }
  }, [open, reopening]);

  const submit = async () => {
    if (!outcome) return;
    setSaving(true); setError('');
    try {
      await api.post(`/projects/${projectId}/outcome`, { outcome, reason: outcome === 'hired_elsewhere' ? (reason || 'other') : null, note: note || null });
      onDone?.(outcome);
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save that — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {reopening ? 'Reopen this listing?' : 'Where do things stand?'}
        <DialogCloseButton onClick={onClose} />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
          {reopening
            ? `"${projectTitle || 'Your project'}" is paused. Reopening puts it back in front of contractors — your brief and photos are kept.`
            : `One tap on "${projectTitle || 'your project'}". There's no wrong answer, and it stops contractors guessing.`}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <RadioGroup value={outcome} onChange={(e) => setOutcome(e.target.value)}>
          {OUTCOMES.filter((o) => !reopening || o.value === 'still_deciding').map((o) => (
            <FormControlLabel
              key={o.value}
              value={o.value}
              control={<Radio />}
              sx={{ alignItems: 'flex-start', mb: 1 }}
              label={(
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{reopening ? 'Yes, reopen it' : o.label}</Typography>
                  <Typography variant="caption" sx={{ color: text.secondary }}>{reopening ? 'Contractors can quote again from today.' : o.hint}</Typography>
                </Box>
              )}
            />
          ))}
        </RadioGroup>

        <Collapse in={outcome === 'hired_elsewhere'}>
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>What got in the way?</Typography>
          <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
            {REASONS.map((r) => (
              <FormControlLabel key={r.value} value={r.value} control={<Radio size="small" />} label={<Typography variant="body2">{r.label}</Typography>} />
            ))}
          </RadioGroup>
        </Collapse>

        {outcome && outcome !== 'still_deciding' && (
          <TextField
            fullWidth multiline minRows={2} sx={{ mt: 2 }}
            label="Anything you'd like us to know? (optional)"
            value={note} onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 1000 }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!outcome || saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {reopening ? 'Reopen listing' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ListingOutcomeDialog;
